import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { axiosApi } from "@/Utils";

import { ROIDataPoint } from "../types/common";

export const getFlightingROIs = ({
  brand,
  category,
  product,
  channels,
  funnelStages,
  startDate,
  endDate,
  geographies = [],
  roiWindow,
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  funnelStages: string[];
  startDate?: string | undefined;
  endDate?: string | undefined;
  geographies?: string[] | undefined;
  roiWindow?: number | undefined;
}): Promise<{ aggregate: ROIDataPoint[]; split: ROIDataPoint[] }> => {
  const payload: Record<string, any> = {
    brand,
    category,
    product,
    channels,
    funnelStages,
    startDate,
    endDate,
    geographies,
    roiWindow,
  };

  return axiosApi.get("/api/v2/flighting", { params: payload, baseURL: INSIGHTS_BACKEND_URL });
};

const _getFlightingROIsNest = async ({
  brand,
  category,
  product,
  channels,
  funnelStages,
  startDate,
  endDate,
  geographies = [],
  roiWindow,
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  funnelStages: string[];
  startDate?: string | undefined;
  endDate?: string | undefined;
  geographies?: string[] | undefined;
  roiWindow?: number | undefined;
}): Promise<{ aggregate: ROIDataPoint[]; split: ROIDataPoint[] }> => {
  const params = new URLSearchParams();

  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (roiWindow) params.append("roiWindow", roiWindow.toString());

  if (channels && channels.length > 0) {
    channels.forEach((channel) => {
      params.append("channels", channel);
    });
  }

  if (funnelStages && funnelStages.length > 0) {
    funnelStages.forEach((stage) => {
      params.append("funnelStages", stage);
    });
  }

  if (geographies && geographies.length > 0) {
    geographies.forEach((geography) => {
      params.append("geographies", geography);
    });
  }

  const response: { aggregate: ROIDataPoint[]; split: ROIDataPoint[] } = await axiosApi.get(
    `/api/flighting?${params.toString()}`,
    {
      baseURL: NEST_BACKEND_URL,
    }
  );

  // Transform the response to flatten nested spend values
  const transformData = (data: any[]): ROIDataPoint[] => {
    return data.map((item) => {
      const transformed: ROIDataPoint = {
        date: item.date,
        roi: item.roi,
        hideROI: item.hideROI,
        ...item.spend,
        totalSpend: item.totalSpend,
        totalRevenue: item.totalRevenue,
      };

      return transformed;
    });
  };

  return {
    aggregate: transformData(response.aggregate || []),
    split: transformData(response.split || []),
  };
};

export const useFlightingROIs = ({
  brand,
  category,
  product,
  channels,
  funnelStages,
  startDate,
  endDate,
  roiWindow,
  geographies = [],
  config,
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  funnelStages: string[];
  startDate: string;
  endDate: string;
  geographies?: string[];
  roiWindow?: number;
  config?: QueryConfig<typeof getFlightingROIs | typeof _getFlightingROIsNest>;
}) => {
  const endpointType = useModuleEndpoint("flighting");

  const queryKey = useMemo(
    () => [
      "flighting-roi-v2",
      brand,
      category,
      product,
      channels,
      startDate,
      endDate,
      geographies,
      roiWindow,
      funnelStages,
      endpointType,
    ],
    [
      brand,
      category,
      product,
      channels,
      startDate,
      endDate,
      geographies,
      roiWindow,
      funnelStages,
      endpointType,
    ]
  );

  const queryFn = useCallback(() => {
    if (endpointType === "new") {
      return _getFlightingROIsNest({
        brand,
        category,
        product,
        channels,
        funnelStages,
        startDate,
        endDate,
        geographies,
        roiWindow,
      });
    } else {
      return getFlightingROIs({
        brand,
        category,
        product,
        channels,
        funnelStages,
        startDate,
        endDate,
        geographies,
        roiWindow,
      });
    }
  }, [
    brand,
    category,
    product,
    channels,
    funnelStages,
    startDate,
    endDate,
    geographies,
    roiWindow,
    endpointType,
  ]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
  });
};
